'use client'

import Link from 'next/link'

import LiteYouTube from '@/blocks/components/LiteYouTube'
import { PublicVideoDto } from '@/server/public-api/types'
import { buildLocalePath } from '@/utils'
import { getYouTubeId, getYouTubeThumbnail } from '@/utils/youtube'

type VediosGallaryProps = {
  vedios: PublicVideoDto[]
  locale: string
}

/**
 * Grid of video cards. When a video has a slug it links to its dedicated watch
 * page (`/library/videos/[slug]`) — this gives Google a crawlable path to a
 * single-video "watch page" that's eligible for video indexing. Slug-less
 * videos keep the inline click-to-play facade so nothing regresses.
 */
export default function VideoGallery({ vedios, locale }: VediosGallaryProps) {
  return (
    <>
      {vedios.map(video => {
        const youtubeId = getYouTubeId(video.link)

        if (!video.slug) {
          return (
            <li key={video.id} className='w-[320px] list-none md:w-[540px]'>
              <LiteYouTube id={youtubeId} title={video.title} />
            </li>
          )
        }

        return (
          <li key={video.id} className='w-[320px] list-none md:w-[540px]'>
            <Link href={buildLocalePath(locale, `/library/videos/${video.slug}`)} className='group block'>
              <div className='relative aspect-video w-full overflow-hidden rounded-lg bg-black'>
                {/* eslint-disable-next-line @next/next/no-img-element -- external YouTube thumbnail; the Next optimizer adds no value for a static YT thumb */}
                <img
                  src={getYouTubeThumbnail(youtubeId)}
                  alt={video.title}
                  loading='lazy'
                  className='absolute inset-0 size-full object-cover'
                />
                <span className='absolute inset-0 grid place-items-center'>
                  <span className='grid size-16 place-items-center rounded-full bg-black/60 transition group-hover:bg-red-600'>
                    <svg aria-hidden viewBox='0 0 24 24' className='size-8 translate-x-[2px] fill-white'>
                      <path d='M8 5v14l11-7z' />
                    </svg>
                  </span>
                </span>
              </div>
              <h2 className='mt-3 text-center text-lg font-semibold text-black group-hover:text-primary'>
                {video.title}
              </h2>
            </Link>
          </li>
        )
      })}
    </>
  )
}
